import React, { Suspense, lazy, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { portfolioData } from '../data/portfolioData';

const FloatingCube = lazy(() => import('./3D/FloatingCube'));

const Hero: React.FC = () => {
  const { hero, personal, stats } = portfolioData;
  const [showCube, setShowCube] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const schedule =
      typeof window.requestIdleCallback === 'function'
        ? (cb: () => void) => window.requestIdleCallback(cb, { timeout: 1800 })
        : (cb: () => void) => window.setTimeout(cb, 600);

    const idleId = schedule(() => setShowCube(true));
    return () => {
      if (typeof window.cancelIdleCallback === 'function' && typeof idleId === 'number') {
        window.cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId as number);
      }
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="min-h-screen py-8 sm:py-12 md:py-16 lg:py-20 flex items-center relative overflow-hidden bg-[#f5f5f5]">
      <div className="absolute inset-0">
        <div className="absolute top-[5%] md:top-20 left-[5%] md:left-20 w-[40vw] md:w-72 h-[40vw] md:h-72 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl animate-float"></div>
        <div className="absolute top-[15%] md:top-40 right-[5%] md:right-20 w-[35vw] md:w-72 h-[35vw] md:h-72 bg-coral-500 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-[10%] md:bottom-20 left-[30%] md:left-1/2 w-[45vw] md:w-72 h-[45vw] md:h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="absolute inset-0 opacity-50 mix-blend-overlay">
        <div className="absolute inset-0 bg-noise"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center min-h-[80vh] sm:min-h-[70vh]">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full flex flex-col justify-center h-full max-w-lg text-center lg:text-left mx-auto lg:mx-0 order-2 lg:order-1"
          >
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 sm:mb-5 rounded-full bg-white/80 border border-gray-200 text-xs sm:text-sm text-gray-700 font-montserrat mx-auto lg:mx-0 w-fit"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              {personal.availability}
            </motion.div>

            <motion.p
              variants={itemVariants}
              className="text-xs sm:text-sm lg:text-base text-gray-500 mb-2 sm:mb-3 font-medium tracking-wider uppercase font-montserrat"
            >
              {hero.eyebrow}
            </motion.p>

            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#0e1c36] mb-3 sm:mb-4 font-oi leading-[1.05]"
            >
              {hero.title}
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-base sm:text-lg text-[#0e1c36]/80 mb-3 font-montserrat font-medium"
            >
              {personal.tagline}
            </motion.p>

            <motion.p
              variants={itemVariants}
              className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-md mx-auto lg:mx-0 font-montserrat"
            >
              {hero.subtitle}
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('#contact')}
                className="px-6 sm:px-8 lg:px-10 py-3.5 sm:py-3 lg:py-3.5 text-sm sm:text-base font-semibold text-black bg-[#FFE600] rounded-full hover:bg-[#FFE600]/90 transition-all duration-200 font-montserrat w-full sm:w-auto shadow-lg hover:shadow-xl active:shadow-md touch-manipulation"
              >
                {hero.primaryCTA}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('#projects')}
                className="px-6 sm:px-8 lg:px-10 py-3.5 sm:py-3 lg:py-3.5 text-sm sm:text-base font-semibold border-2 border-gray-900 text-gray-900 rounded-full hover:bg-gray-900 hover:text-white transition-all duration-200 font-montserrat w-full sm:w-auto shadow-lg hover:shadow-xl active:shadow-md touch-manipulation"
              >
                {hero.secondaryCTA}
              </motion.button>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6 font-montserrat"
            >
              {['React', 'Next.js', 'Node.js', 'TypeScript'].map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1 text-xs font-medium text-gray-700 bg-white/80 border border-gray-200 rounded-full"
                >
                  {tech}
                </span>
              ))}
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-3 sm:gap-4 pt-6 border-t border-gray-200 font-montserrat"
            >
              {stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-black">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-600 leading-tight">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="relative hidden lg:block h-96 lg:h-[500px] xl:h-[600px] order-1 lg:order-2"
          >
            <Suspense fallback={<div className="w-full h-full" aria-hidden />}>
              {showCube ? <FloatingCube /> : null}
            </Suspense>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.8 }}
        className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.button
          onClick={() => scrollToSection('#about')}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="p-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-all duration-200 touch-manipulation"
          aria-label="Aller à la section à propos"
        >
          <ChevronDownIcon className="h-5 w-5 text-gray-700" />
        </motion.button>
      </motion.div>
    </section>
  );
};

export default Hero;
